import React, { useState } from 'react';
import {
  Button,
  Form,
  FormGroup,
  FormHelperText,
  HelperText,
  HelperTextItem,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  TextInput,
} from '@patternfly/react-core';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import type { ApiResponseDomainTld } from '../../../types/api/ApiResponseDomainPricing';

interface AddTldModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Re-fetch the matrix after a TLD is added (the new TLD becomes a new row). */
  onAdded: () => void;
}

// Adds a TLD to the registry (POST /admin/products/domains/tlds) — this is how a new TLD becomes
// priceable. The backend normalizes the value (lowercases, strips a leading dot; multi-label allowed)
// and re-validates; a field error (missing/invalid/duplicate) arrives as the data.tld string.
function AddTldModal({ isOpen, onClose, onAdded }: AddTldModalProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();

  const [tldDraft, setTldDraft] = useState('');
  const [labelDraft, setLabelDraft] = useState('');
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  const tldTrimmed = tldDraft.trim();
  const labelTrimmed = labelDraft.trim();
  const isTldValid = tldTrimmed.length > 0;

  const handleSave = async () => {
    if (!isTldValid) {
      return;
    }
    setIsSaving(true);
    setServerError(null);
    try {
      const response = await apiFetch('/admin/products/domains/tlds', {
        method: 'POST',
        body: JSON.stringify({ tld: tldTrimmed, ...(labelTrimmed ? { label: labelTrimmed } : {}) }),
      });
      // Branch on the JSend body.status, NEVER response.ok.
      const body = (await response.json().catch(() => null)) as ApiResponseDomainTld | null;
      if (body?.status === 'success' && body.data?.tld && typeof body.data.tld === 'object') {
        addNotification({
          title: 'TLD added',
          srTitle: 'Success alert',
          description: `${body.data.tld.label} is now priceable.`,
          variant: 'success',
        });
        setDrawerOpen(true);
        onAdded();
        onClose();
        return;
      }
      // On failure data.tld is the field-error string.
      const fieldError = typeof body?.data?.tld === 'string' ? body.data.tld : null;
      setServerError(fieldError ?? body?.message ?? 'Could not add the TLD. Please try again.');
      addNotification({
        title: 'Could not add TLD',
        srTitle: 'Error alert',
        description: fieldError ?? body?.message ?? 'Could not add the TLD. Please try again.',
        variant: 'danger',
      });
      setDrawerOpen(true);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'An unexpected error occurred.';
      setServerError(message);
      addNotification({ title: 'Could not add TLD', srTitle: 'Error alert', description: message, variant: 'danger' });
      setDrawerOpen(true);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal variant="small" isOpen={isOpen} onClose={onClose} aria-label="Add a TLD">
      <ModalHeader title="Add a TLD" />
      <ModalBody>
        <Form
          onSubmit={(event) => {
            event.preventDefault();
            void handleSave();
          }}
        >
          <FormGroup label="TLD" fieldId="add-tld-input" isRequired>
            <TextInput
              id="add-tld-input"
              type="text"
              value={tldDraft}
              onChange={(_event, value) => {
                setTldDraft(value);
                if (serverError) {
                  setServerError(null);
                }
              }}
              validated={serverError ? 'error' : 'default'}
              isDisabled={isSaving}
              placeholder="e.g. io or co.uk"
              aria-label="TLD"
            />
            {serverError && (
              <FormHelperText>
                <HelperText>
                  <HelperTextItem variant="error">{serverError}</HelperTextItem>
                </HelperText>
              </FormHelperText>
            )}
          </FormGroup>
          <FormGroup label="Label (optional)" fieldId="add-tld-label-input">
            <TextInput
              id="add-tld-label-input"
              type="text"
              value={labelDraft}
              onChange={(_event, value) => setLabelDraft(value)}
              isDisabled={isSaving}
              placeholder="e.g. .io (defaults from the TLD)"
              aria-label="TLD display label"
            />
          </FormGroup>
        </Form>
      </ModalBody>
      <ModalFooter>
        <Button variant="primary" onClick={() => void handleSave()} isLoading={isSaving} isDisabled={isSaving || !isTldValid}>
          Add TLD
        </Button>
        <Button variant="link" onClick={onClose} isDisabled={isSaving}>
          Cancel
        </Button>
      </ModalFooter>
    </Modal>
  );
}

export default AddTldModal;
